import {
  ActionIcon,
  AppShell,
  Combobox,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
  useCombobox,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import type { Icon } from '@tabler/icons-react';
import {
  IconBook,
  IconBoxMultiple,
  IconChartPie,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconCodeDots,
  IconComponents,
  IconSettingsAutomation,
  IconDeviceAnalytics,
  IconDeviceDesktop,
  IconEdit,
  IconFileSettings,
  IconHome2,
  IconInfoCircle,
  IconPlayerPlay,
  IconCube,
  IconList,
  IconMessages,
  IconMoon,
  IconSettings2,
  IconGitPullRequest,
  IconReportAnalytics,
  IconSettings,
  IconSparkles,
  IconSun,
  IconTerminal2,
} from '@tabler/icons-react';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import { AGENT_EXCLUSIVE_ROUTES, SHARED_ROUTES } from '~/constants/routes';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useViewModeValue } from '~/hooks/context/useViewMode';
import { api } from '~/trpc-extra/api';
import { useSupportMenuItems } from './useSupportMenuItems';
import { FullLogo } from './FullLogo';
import { UsageLimitIndicator } from './UsageLimitIndicator';
import { ViewModeSwitcher } from './ViewModeSwitcher';

// Route configuration for Agent navigation items
const AGENT_ROUTE_CONFIG: Record<
  (typeof AGENT_EXCLUSIVE_ROUTES)[number] | (typeof SHARED_ROUTES)[number],
  { icon: Icon; label: string; shortLabel?: string; isBeta?: boolean; isAdmin?: boolean }
> = {
  home: { icon: IconHome2, label: 'Home', shortLabel: 'Home' },
  agents: { icon: IconSparkles, label: 'Agents', shortLabel: 'Agents' },
  tests: { icon: IconList, label: 'Tests', shortLabel: 'Tests' },
  'test-management': { icon: IconDeviceAnalytics, label: 'Test Plan', shortLabel: 'Test Plan' },
  projects: { icon: IconBoxMultiple, label: 'Suites', shortLabel: 'Suites' },
  'test-config': { icon: IconFileSettings, label: 'Test Settings', shortLabel: 'Settings' },
  'playwright/history': { icon: IconReportAnalytics, label: 'All Runs', shortLabel: 'All Runs' },
  'playwright/schedulers': { icon: IconClock, label: 'Schedule', shortLabel: 'Schedule' },
  'cli-sessions': { icon: IconTerminal2, label: 'CLI Sessions', shortLabel: 'CLI' },
  cloud: { icon: IconSettingsAutomation, label: 'AI & Cloud', shortLabel: 'AI & Cloud' },
  environments: { icon: IconCube, label: 'Environments', shortLabel: 'Envs' },
  testData: { icon: IconCodeDots, label: 'Test Data', shortLabel: 'Data' },
  settings: { icon: IconSettings, label: 'Settings', shortLabel: 'Settings' },
  pr: { icon: IconGitPullRequest, label: 'Pull Requests', shortLabel: 'PRs' },
} as const;

// Agent navigation items - excludes 'settings' which is shown in footer for agent view
const AGENT_NAV_ITEMS_ALL = [...AGENT_EXCLUSIVE_ROUTES, ...SHARED_ROUTES]
  .filter((route) => route !== 'settings')
  .map((route) => {
    const config = AGENT_ROUTE_CONFIG[route];
    return {
      icon: config.icon,
      label: config.label,
      ...(config.shortLabel && { shortLabel: config.shortLabel }),
      href:
        route === 'test-management' ?
          (projectId: string) => `/project/${projectId}/test-management`
        : (projectId: string) => `/project/${projectId}/${route}`,
      children:
        route === 'test-management' ?
          [
            {
              icon: IconList,
              label: 'Test Cases',
              href: (projectId: string) => `/project/${projectId}/test-management/cases`,
            },
          ]
        : [],
      ...(config.isBeta && { isBeta: true }),
      ...(config.isAdmin && { isAdmin: true }),
    };
  });

// TODO: probably shouldn't use any here
type NavItem = {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  label: string;
  shortLabel?: string;
  href: (projectId: string) => string;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  children: { icon: any; label: string; href: (projectId: string) => string }[];
  onClick?: () => void;
  isBeta?: boolean;
  isAdmin?: boolean;
  matchPaths?: ((projectId: string) => string)[];
};

// Top-level items: Home, Agents
const AGENT_NAV_ITEMS_TOP = AGENT_NAV_ITEMS_ALL.filter((item) => ['Home', 'Agents'].includes(item.label));

// Runs section: single top-level link to run history
const AGENT_NAV_ITEMS_RUNS: NavItem = {
  icon: IconPlayerPlay,
  label: 'Runs',
  shortLabel: 'Runs',
  href: (projectId: string) => `/project/${projectId}/playwright/history`,
  children: [],
};

// Test Management section: Cases, Run Groups
const AGENT_NAV_ITEMS_TCM = AGENT_NAV_ITEMS_ALL.find((item) => item.label === 'Test Plan');

// Test Management section: Tests, Suites, Test Settings, CLI Sessions
const AGENT_NAV_ITEMS_PLAYWRIGHT = AGENT_NAV_ITEMS_ALL.filter((item) =>
  ['Tests', 'Suites', 'Test Settings', 'CLI Sessions'].includes(item.label),
);

// Configuration section: AI & Cloud, Schedule, Environments
const AGENT_NAV_ITEMS_CONFIG = ['AI & Cloud', 'Schedule', 'Environments']
  .map((label) => AGENT_NAV_ITEMS_ALL.find((item) => item.label === label))
  .filter((item): item is NavItem => item !== undefined);

const AGENT_NAV_ITEMS_TCM_CHILDREN: NavItem[] =
  AGENT_NAV_ITEMS_TCM?.children.map((child) => ({
    ...child,
    children: [],
    shortLabel: child.label,
  })) ?? [];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type FooterNavItemBase = { icon: any; label: string };
type FooterNavItemWithHref = FooterNavItemBase & { href: (projectId: string) => string };
type FooterNavItemWithMenu = FooterNavItemBase & {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  menuItems: { label: string; icon: any; rightSection?: React.ReactNode; onClick: () => void }[];
};
type FooterNavItemWithClick = FooterNavItemBase & { onClick: () => void };
type FooterNavItem = FooterNavItemWithHref | FooterNavItemWithMenu | FooterNavItemWithClick;

/**
 * Finds the longest child path that matches the current route.
 * Returns the parent's href if no children match.
 */
const findLongestChildPath = (navItem: NavItem, currentPath: string): string => {
  return navItem.children.reduce((longestPath, child) => {
    const childPath = child.href('[projectId]');
    if (currentPath.startsWith(childPath) && childPath.length > longestPath.length) {
      return childPath;
    }
    return longestPath;
  }, navItem.href('[projectId]'));
};

/**
 * Determines the effective path for a nav item, considering matchPaths.
 */
const getEffectivePath = ({
  navItem,
  currentPath,
  basePath,
}: {
  navItem: NavItem;
  currentPath: string;
  basePath: string;
}): string => {
  const matchingPath = navItem.matchPaths?.find((matchPath) => currentPath.startsWith(matchPath('[projectId]')));
  return matchingPath ? matchingPath('[projectId]') : basePath;
};

/**
 * Finds the longest nav item path that matches the current route.
 */
const findActiveNavPath = (navItems: NavItem[], currentPath: string): string => {
  return navItems.reduce((longestMatch, navItem) => {
    const childPath = findLongestChildPath(navItem, currentPath);
    const effectivePath = getEffectivePath({ navItem, currentPath, basePath: childPath });

    if (currentPath.startsWith(effectivePath) && effectivePath.length > longestMatch.length) {
      return effectivePath;
    }
    return longestMatch;
  }, '');
};

function SidebarProjectSwitcher({
  projectId,
  projects,
  onProjectSwitch,
  onManageProject,
}: {
  projectId: string;
  projects: { value: string; label: string }[];
  onProjectSwitch: (projectId: string) => void;
  onManageProject: () => void;
}) {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
    },
    onDropdownOpen: () => {
      combobox.updateSelectedOptionIndex('active');
    },
  });
  const [manageTooltipOpened, setManageTooltipOpened] = useState(false);

  const currentProject = useMemo(() => projects.find((project) => project.value === projectId), [projectId, projects]);

  const sortedProjects = useMemo(() => [...projects].sort((a, b) => a.label.localeCompare(b.label)), [projects]);

  const options = sortedProjects.map((project) => {
    const isCurrent = project.value === projectId;

    return (
      <Combobox.Option
        value={project.value}
        key={project.value}
        active={isCurrent}
        className="w-full max-w-full rounded-lg px-3 py-2 transition-colors hover:bg-black/[0.04] data-[combobox-active]:bg-black/[0.06] dark:hover:bg-white/[0.06] dark:data-[combobox-active]:bg-white/[0.08]"
      >
        <div className="grid w-full max-w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 overflow-hidden">
          <div className="max-w-full min-w-0 overflow-hidden">
            <Tooltip
              label={project.label}
              disabled={project.label.length < 28}
              withArrow
              position="right"
            >
              <div className="block w-full max-w-full min-w-0 overflow-hidden text-[13px] font-medium text-ellipsis whitespace-nowrap text-gray-900 dark:text-gray-50">
                {project.label}
              </div>
            </Tooltip>
            <div className="w-full min-w-0 overflow-hidden text-[11px] text-ellipsis whitespace-nowrap text-gray-500 dark:text-gray-400">
              {isCurrent ? 'Current project' : 'Open project'}
            </div>
          </div>
          {isCurrent && (
            <div className="shrink-0 text-[var(--mantine-color-dimmed)]">
              <IconCheck
                size={14}
                stroke={2}
              />
            </div>
          )}
        </div>
      </Combobox.Option>
    );
  });

  return (
    <Combobox
      store={combobox}
      width={320}
      position="bottom-start"
      onOptionSubmit={(value) => {
        combobox.closeDropdown();
        if (value !== projectId) {
          onProjectSwitch(value);
        }
      }}
      withinPortal
    >
      <Combobox.Target>
        <UnstyledButton
          onClick={() => combobox.toggleDropdown()}
          aria-label="Project switcher"
          className="flex w-full items-center gap-2 rounded-md px-3 py-1 text-left text-[13px] font-medium text-gray-800 transition-colors hover:bg-black/[0.04] focus:bg-black/[0.04] dark:text-gray-100 dark:hover:bg-white/[0.06] dark:focus:bg-white/[0.06]"
        >
          <Tooltip
            label={currentProject?.label ?? 'Select project'}
            disabled={!currentProject?.label || currentProject.label.length < 24}
            withArrow
            position="right"
          >
            <span className="min-w-0 flex-1 truncate">{currentProject?.label ?? 'Select project'}</span>
          </Tooltip>
          <IconChevronDown
            size={14}
            stroke={1.8}
            className="shrink-0 text-gray-500 dark:text-gray-400"
          />
        </UnstyledButton>
      </Combobox.Target>

      <Combobox.Dropdown className="overflow-hidden rounded-2xl border border-black/10 bg-[var(--mantine-color-body)] shadow-2xl dark:border-white/10">
        <div className="flex items-start justify-between gap-3 border-b border-black/5 px-3 pt-3 pb-2 dark:border-white/10">
          <div>
            <Text
              size="sm"
              fw={700}
            >
              Select Stably Project
            </Text>
            <Text
              size="xs"
              c="dimmed"
              className="pt-0.5"
            >
              Switch to another project in this organization.
            </Text>
          </div>
          <Tooltip
            label="Manage project"
            withArrow
            position="top"
            opened={manageTooltipOpened}
            withinPortal={false}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Manage project"
              onMouseEnter={() => setManageTooltipOpened(true)}
              onMouseLeave={() => setManageTooltipOpened(false)}
              onFocus={() => setManageTooltipOpened(true)}
              onBlur={() => setManageTooltipOpened(false)}
              onClick={() => {
                setManageTooltipOpened(false);
                combobox.closeDropdown();
                window.requestAnimationFrame(() => {
                  onManageProject();
                });
              }}
            >
              <IconSettings2
                size={14}
                stroke={1.8}
              />
            </ActionIcon>
          </Tooltip>
        </div>

        <Combobox.Options>
          <ScrollArea.Autosize
            mah={280}
            scrollbarSize={6}
            className="overflow-x-hidden px-2 py-2"
            type="never"
          >
            <div className="space-y-1">
              {options.length > 0 ? options : <Combobox.Empty>No matching projects</Combobox.Empty>}
            </div>
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

function ColorSchemeToggle() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  return (
    <div className="flex items-center gap-1">
      <ActionIcon
        variant={colorScheme === 'auto' ? 'light' : 'transparent'}
        color={colorScheme === 'auto' ? 'blue' : 'gray'}
        size="md"
        onClick={() => setColorScheme('auto')}
        title="System"
        className="!border-none !outline-none"
      >
        <IconDeviceDesktop
          size={18}
          stroke={1.5}
        />
      </ActionIcon>
      <ActionIcon
        variant={colorScheme === 'light' ? 'light' : 'transparent'}
        color={colorScheme === 'light' ? 'blue' : 'gray'}
        size="md"
        onClick={() => setColorScheme('light')}
        title="Light mode"
        className="!border-none !outline-none"
      >
        <IconSun
          size={18}
          stroke={1.5}
        />
      </ActionIcon>
      <ActionIcon
        variant={colorScheme === 'dark' ? 'light' : 'transparent'}
        color={colorScheme === 'dark' ? 'blue' : 'gray'}
        size="md"
        onClick={() => setColorScheme('dark')}
        title="Dark mode"
        className="!border-none !outline-none"
      >
        <IconMoon
          size={18}
          stroke={1.5}
        />
      </ActionIcon>
    </div>
  );
}

export function Sidebar({ isCollapsed = false }: { isCollapsed?: boolean }) {
  const colorScheme = useComputedColorScheme();
  const projectId = useProjectId() || '';
  const router = useRouter();
  const readyToNav = projectId && router.isReady;
  const storedViewMode = useViewModeValue(projectId || undefined);
  const { activeOrg, isAdmin, orgMetadata } = useAuthData();
  // On the welcome page (no project yet), the localStorage atom defaults to 'classic'
  // and useViewModeSync can't run without a projectId. Use org metadata as fallback
  // so agent-locked orgs see the Agent 2.0 sidebar before creating their first project.
  const viewMode = !projectId && orgMetadata?.defaultViewMode === 'agent' ? 'agent' : storedViewMode;
  const { data: hasUserflowAndFeatures } = api.userFlow.hasUserflowAndFeatures.useQuery(
    { projectId },
    { enabled: !!projectId },
  );
  const { data: orgProjects } = api.project.listProjects.useQuery(
    { teamId: activeOrg?.orgId || '' },
    { enabled: !!activeOrg?.orgId },
  );

  const supportMenuItems = useSupportMenuItems({
    projectId,
    pathname: router.asPath || undefined,
  });

  const projectSwitcherOptions = useMemo(
    () =>
      (orgProjects ?? []).map((project) => ({
        value: project.id,
        label: project.name,
      })),
    [orgProjects],
  );

  const shouldShowProjectSwitcher = !!projectId && (orgProjects?.length ?? 0) > 1;

  const handleProjectSwitch = useCallback(
    (newProjectId: string | null) => {
      if (!newProjectId || newProjectId === projectId) {
        return;
      }

      const hasProjectBoundCallbackParams = ['code', 'state', 'installation_id', 'setup_action'].some(
        (param) => param in router.query,
      );

      if (hasProjectBoundCallbackParams) {
        router.push(`/project/${newProjectId}`).catch(() => undefined);
        return;
      }

      const nextPath = router.asPath.replace(/^\/project\/[^/?#]+/, `/project/${newProjectId}`);
      router.push(nextPath).catch(() => undefined);
    },
    [projectId, router.asPath, router.query, router],
  );

  const defaultNavItems: NavItem[] = useMemo(
    () => [
      {
        icon: IconHome2,
        label: 'Home',
        href: (projectId: string) => `/project/${projectId}`,
        children: [],
      },
      {
        icon: IconBook,
        label: 'Knowledge',
        href: (projectId: string) => `/project/${projectId}/knowledge`,
        children: [],
        isAdmin: false,
      },
      // {
      //   icon: IconPumpkinScary,
      //   label: 'No Code 2.0',
      //   href: (projectId: string) => `/project/${projectId}/t/mock`,
      //   children: [],
      //   isBeta: true,
      //   isAdmin: true,
      // },
      ...(hasUserflowAndFeatures ?
        [
          {
            icon: IconChartPie,
            label: 'Coverage',
            href: (projectId: string) => `/project/${projectId}/coverage`,
            children: [],
          },
        ]
      : []),
      {
        icon: IconSettings,
        label: 'Settings',
        href: (projectId: string) => `/project/${projectId}/settings`,
        children: [],
      },
    ],
    [hasUserflowAndFeatures],
  );

  const creationNavItems: NavItem[] = useMemo(
    () => [
      {
        icon: IconList,
        label: 'Test Cases',
        href: (projectId: string) => `/project/${projectId}/test`,
        children: [],
      },
      {
        icon: IconEdit,
        label: 'Drafts',
        href: (projectId: string) => `/project/${projectId}/testDraft`,
        children: [],
      },
      {
        icon: IconCodeDots,
        label: 'Test Data',
        href: (projectId: string) => `/project/${projectId}/testData`,
        children: [],
        isAdmin: false,
      },
      {
        icon: IconComponents,
        label: 'Components',
        href: (projectId: string) => `/project/${projectId}/components`,
        children: [],
        isAdmin: false,
      },
    ],
    [],
  );

  const executionNavItems: NavItem[] = useMemo(
    () => [
      {
        icon: IconReportAnalytics,
        label: 'Run History',
        href: (projectId: string) => `/project/${projectId}/history`,
        children: [],
      },
      {
        icon: IconBoxMultiple,
        label: 'Test Suites',
        href: (projectId: string) => `/project/${projectId}/testSuite`,
        children: [],
      },
      {
        icon: IconDeviceAnalytics,
        label: 'Analytics',
        href: (projectId: string) => `/project/${projectId}/dashboard`,
        children: [],
      },
    ],
    [],
  );

  const footerNavItems = useMemo(
    (): FooterNavItem[] => [
      // Settings appears in footer only for agent view
      ...(viewMode === 'agent' ?
        [
          {
            icon: IconSettings,
            label: 'Settings',
            href: (projectId: string) => `/project/${projectId}/settings`,
          } satisfies FooterNavItemWithHref,
        ]
      : []),
      {
        icon: IconMessages,
        label: 'Support',
        menuItems: supportMenuItems,
      },
    ],
    [viewMode, supportMenuItems],
  );

  const activeNavPath = useMemo(() => {
    const navItemsToCheck =
      viewMode === 'agent' ?
        [...AGENT_NAV_ITEMS_ALL, AGENT_NAV_ITEMS_RUNS]
      : [...defaultNavItems, ...creationNavItems, ...executionNavItems];

    return findActiveNavPath(navItemsToCheck, router.pathname);
  }, [router.pathname, viewMode, defaultNavItems, creationNavItems, executionNavItems]);

  const getNavListItem = (navItems: NavItem[]) => {
    return navItems
      .filter((navItem) => navItem.isAdmin === undefined || !navItem.isAdmin || isAdmin)
      .map((navItem) => {
        const parentActive =
          navItem.href('[projectId]') === activeNavPath ||
          navItem.matchPaths?.some((matchPath) => matchPath('[projectId]') === activeNavPath);

        const childActive = navItem.children.some((child) => child.href('[projectId]') === activeNavPath);
        const active = parentActive && !childActive;

        // Add refresh parameter to agents link to force panel reopen on same-page clicks
        const getHref = () => {
          if (!readyToNav) {
            return '#';
          }
          const baseHref = navItem.href(projectId);
          if (baseHref.includes('/agents')) {
            return `${baseHref}?refresh=${Date.now()}`;
          }
          return baseHref;
        };

        if (isCollapsed) {
          // Agent view: larger click area with hover scale effect
          if (viewMode === 'agent') {
            return (
              <Link
                key={navItem.label}
                href={getHref()}
                onClick={navItem.onClick}
                className="group/navitem mb-1 flex w-full cursor-pointer flex-col items-center rounded-md py-1"
              >
                <ActionIcon
                  component="div"
                  variant="subtle"
                  size="md"
                  color={active ? 'blue' : 'gray'}
                  className={clsx(
                    'group-hover/navitem:bg-[var(--mantine-color-default-hover)]',
                    !active &&
                      'text-[#3d4350] group-hover/navitem:text-[var(--mantine-color-gray-7)] dark:text-[var(--mantine-color-gray-4)] dark:group-hover/navitem:text-[var(--mantine-color-gray-4)]',
                  )}
                >
                  <navItem.icon
                    size="1.1rem"
                    stroke={1.5}
                  />
                </ActionIcon>
                <span
                  className={clsx(
                    'mt-0.5 text-center text-[10px] font-semibold tracking-tight',
                    active ?
                      'text-[var(--mantine-color-blue-7)] dark:text-[var(--mantine-color-blue-4)]'
                    : 'text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]',
                  )}
                >
                  {navItem.shortLabel || navItem.label}
                </span>
              </Link>
            );
          }

          return (
            <div
              key={navItem.label}
              className="mb-[0.5rem] flex flex-col items-center"
            >
              <ActionIcon
                component={Link}
                href={getHref()}
                onClick={navItem.onClick}
                variant="subtle"
                size="md"
                color={active ? 'blue' : 'gray'}
                className={clsx(!active && 'text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]')}
              >
                <navItem.icon
                  size="1.2rem"
                  stroke={1.5}
                />
              </ActionIcon>
              <span
                className={clsx(
                  'mt-0.5 text-center text-[10px] font-semibold',
                  active ?
                    'text-[var(--mantine-color-blue-7)] dark:text-[var(--mantine-color-blue-4)]'
                  : 'text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]',
                )}
              >
                {navItem.shortLabel || navItem.label}
              </span>
            </div>
          );
        }

        const rightSection =
          navItem.isBeta ?
            <div className="pointer-events-none rounded-md bg-purple-500/20 px-[6px] py-[2px] text-[10px] text-[var(--mantine-color-text)]">
              BETA
            </div>
          : navItem.isAdmin ?
            <div className="pointer-events-none rounded-md border border-amber-300 bg-amber-100 px-[6px] py-[2px] text-[10px] font-semibold text-amber-900 dark:border-amber-500/50 dark:bg-amber-500/20 dark:text-amber-100">
              ADMIN
            </div>
          : undefined;

        const labelContent =
          navItem.label === 'Suites' ?
            <span className="flex items-center gap-1">
              {navItem.label}
              <Tooltip
                label="Called 'Projects' in Playwright — we renamed it to avoid confusion with Stably projects"
                withArrow
                multiline
                w={220}
                position="right"
              >
                <IconInfoCircle
                  size="0.85rem"
                  stroke={1.5}
                  className="cursor-help text-[var(--mantine-color-gray-5)]"
                />
              </Tooltip>
            </span>
          : navItem.label;

        const navLinkProps = {
          active: false,
          label: labelContent,
          component: Link,
          href: getHref(),
          ...(navItem.onClick && { onClick: navItem.onClick }),
          ...(navItem.children.length > 0 && { defaultOpened: childActive }),
          childrenOffset: 0,
          classNames: {
            label: `${viewMode === 'agent' ? 'text-[13px]' : 'text-[12px]'} ${active ? 'font-medium' : 'font-normal'} ${
              active ?
                'text-[var(--mantine-color-blue-7)] dark:text-[var(--mantine-color-blue-4)]'
              : 'text-gray-800 dark:text-gray-100'
            }`,
            root: `rounded-lg px-3 py-1 [&:hover_.mantine-NavLink-label]:text-[var(--mantine-color-text)] [&:hover_.mantine-NavLink-section]:text-[var(--mantine-color-text)] ${active ? 'bg-[var(--mantine-color-blue-light)]' : ''}`,
            section:
              active ?
                'text-[var(--mantine-color-blue-7)] dark:text-[var(--mantine-color-blue-4)]'
              : 'text-gray-700 dark:text-gray-200',
          },
          leftSection: (
            <navItem.icon
              size="1.1rem"
              stroke={1.5}
              className="mt-px"
            />
          ),
          rightSection: rightSection,
        };

        return (
          <div
            key={navItem.label}
            className="my-[0.35rem] px-2"
          >
            <NavLink {...navLinkProps}>
              {navItem.children.length > 0 &&
                navItem.children.map((child) => {
                  const childActive = child.href('[projectId]') === activeNavPath;

                  return (
                    <div
                      key={child.label}
                      className="my-[0.35rem] px-1"
                    >
                      <NavLink
                        active={false}
                        label={child.label}
                        component={Link}
                        href={readyToNav ? child.href(projectId) : '#'}
                        leftSection={
                          <child.icon
                            size="0.9rem"
                            stroke={1.5}
                          />
                        }
                        classNames={{
                          label: `text-[12px] ${childActive ? 'font-medium' : 'font-normal'} ${
                            childActive ?
                              'text-[var(--mantine-color-blue-7)] dark:text-[var(--mantine-color-blue-4)]'
                            : 'text-gray-800 dark:text-gray-100'
                          }`,
                          section:
                            childActive ?
                              'text-[var(--mantine-color-blue-7)] dark:text-[var(--mantine-color-blue-4)]'
                            : 'text-gray-700 dark:text-gray-200',
                          root: `rounded-lg pl-8 pr-3 py-1 [&:hover_.mantine-NavLink-label]:text-[var(--mantine-color-text)] [&:hover_.mantine-NavLink-section]:text-[var(--mantine-color-text)] ${childActive ? 'bg-[var(--mantine-color-blue-light)]' : ''}`,
                        }}
                      />
                    </div>
                  );
                })}
            </NavLink>
          </div>
        );
      });
  };

  if (isCollapsed) {
    return (
      <>
        <AppShell.Section
          grow
          component={ScrollArea}
          className={clsx(
            'relative mb-4',
            viewMode === 'agent' &&
              '[&_.mantine-ScrollArea-scrollbar]:rounded-full [&_.mantine-ScrollArea-scrollbar]:bg-transparent [&_.mantine-ScrollArea-scrollbar]:opacity-0 [&_.mantine-ScrollArea-scrollbar]:transition-opacity [&_.mantine-ScrollArea-scrollbar]:duration-200 [&_.mantine-ScrollArea-scrollbar]:hover:bg-gray-100 [&_.mantine-ScrollArea-scrollbar]:dark:hover:bg-gray-800 [&_.mantine-ScrollArea-thumb]:rounded-full [&_.mantine-ScrollArea-thumb]:bg-gray-300 [&_.mantine-ScrollArea-thumb]:hover:bg-gray-400 [&_.mantine-ScrollArea-thumb]:dark:bg-gray-600 [&_.mantine-ScrollArea-thumb]:dark:hover:bg-gray-500 [&:hover_.mantine-ScrollArea-scrollbar]:opacity-100',
          )}
          scrollbarSize={viewMode === 'agent' ? 6 : 0}
          type={viewMode === 'agent' ? 'auto' : 'never'}
        >
          <div className="sticky top-0 z-10 bg-[var(--mantine-color-body)]">
            <div className="flex w-full items-center justify-center pt-1 pb-4">
              <svg
                width="28"
                height="28"
                viewBox="0 0 2378 2378"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_16_47)">
                  <path
                    d="M1188.94 618.645C1500.69 618.645 1753.36 871.322 1753.36 1183.04C1753.36 1494.72 1500.69 1747.39 1188.94 1747.39C877.252 1747.39 624.547 1494.72 624.547 1183.04C624.547 871.317 877.252 618.645 1188.94 618.645Z"
                    fill={colorScheme === 'dark' ? '#FCF5E5' : 'black'}
                  />
                  <path
                    d="M1188.94 0C1040.81 0 899.46 28.4581 768.619 78.1742C797.202 107.203 820.84 140.992 838.99 178.052C948.861 139.818 1066.35 118.055 1189 118.055C1779.5 118.055 2259.85 598.561 2259.85 1188.94C2259.85 1779.31 1779.5 2259.91 1188.94 2259.91C598.501 2259.91 118.12 1779.37 118.12 1189C118.12 990.106 173.607 804.408 268.409 644.63C235.919 619.759 207.271 589.937 184.328 555.958C68.2136 739.493 0 956.186 0 1189C0 1844.6 533.33 2378.01 1188.94 2378.01C1844.6 2378.01 2378 1844.61 2378 1189C2378 533.395 1844.6 0 1188.94 0Z"
                    fill={colorScheme === 'dark' ? '#FCF5E5' : 'black'}
                  />
                  <path
                    d="M498.624 111.11C633.209 111.11 742.287 220.188 742.287 354.773C742.287 489.358 633.209 598.436 498.624 598.436C364.071 598.436 254.961 489.358 254.961 354.773C254.961 220.188 364.071 111.11 498.624 111.11Z"
                    fill={colorScheme === 'dark' ? '#FCF5E5' : 'black'}
                  />
                </g>
                <defs>
                  <clipPath id="clip0_16_47">
                    <rect
                      width="2378"
                      height="2378"
                      fill="white"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
            {viewMode === 'agent' && (
              <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-4 translate-y-full bg-gradient-to-b from-[var(--mantine-color-body)] to-transparent" />
            )}
          </div>
          <div className="pt-2" />
          <div className="flex flex-col items-center">
            {viewMode === 'agent' ?
              <>
                {getNavListItem(AGENT_NAV_ITEMS_TOP)}
                {getNavListItem([AGENT_NAV_ITEMS_RUNS])}
                {isAdmin && getNavListItem(AGENT_NAV_ITEMS_TCM_CHILDREN)}
                {getNavListItem(AGENT_NAV_ITEMS_PLAYWRIGHT)}
                {getNavListItem(AGENT_NAV_ITEMS_CONFIG)}
              </>
            : <>
                {getNavListItem(defaultNavItems)}
                {getNavListItem(creationNavItems)}
                {getNavListItem(executionNavItems)}
              </>
            }
          </div>
          <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-4 bg-gradient-to-t from-[var(--mantine-color-body)] to-transparent" />
        </AppShell.Section>
        <AppShell.Section>
          <UsageLimitIndicator isCollapsed />
          <div className="flex flex-col items-center">
            {footerNavItems.map((navItem) => {
              const NavIcon = navItem.icon;
              const hasHref = 'href' in navItem && navItem.href;
              const hasMenu = 'menuItems' in navItem && navItem.menuItems;

              // Agent view: match main nav item styling with labels
              if (viewMode === 'agent') {
                if (hasMenu) {
                  return (
                    <Menu
                      key={navItem.label}
                      position="right-end"
                      withArrow
                    >
                      <Menu.Target>
                        <div className="group/navitem mb-1 flex w-full cursor-pointer flex-col items-center rounded-md py-1">
                          <ActionIcon
                            component="div"
                            variant="subtle"
                            size="md"
                            color="gray"
                            className="text-[#3d4350] group-hover/navitem:bg-[var(--mantine-color-default-hover)] group-hover/navitem:text-[var(--mantine-color-gray-7)] dark:text-[var(--mantine-color-gray-4)] dark:group-hover/navitem:text-[var(--mantine-color-gray-4)]"
                          >
                            <NavIcon
                              size="1.1rem"
                              stroke={1.5}
                            />
                          </ActionIcon>
                          <span className="mt-0.5 text-center text-[10px] font-semibold tracking-tight text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]">
                            {navItem.label}
                          </span>
                        </div>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {navItem.menuItems.map((item) => (
                          <Menu.Item
                            key={item.label}
                            leftSection={<item.icon size={14} />}
                            rightSection={item.rightSection}
                            onClick={item.onClick}
                          >
                            {item.label}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  );
                }

                return (
                  <div
                    key={navItem.label}
                    className="group/navitem mb-1 flex w-full cursor-pointer flex-col items-center rounded-md py-1"
                    onClick={hasHref || !('onClick' in navItem) ? undefined : navItem.onClick}
                  >
                    {hasHref ?
                      <Link
                        href={readyToNav ? navItem.href(projectId) : '#'}
                        className="flex flex-col items-center"
                      >
                        <ActionIcon
                          component="div"
                          variant="subtle"
                          size="md"
                          color="gray"
                          className="text-[#3d4350] group-hover/navitem:bg-[var(--mantine-color-default-hover)] group-hover/navitem:text-[var(--mantine-color-gray-7)] dark:text-[var(--mantine-color-gray-4)] dark:group-hover/navitem:text-[var(--mantine-color-gray-4)]"
                        >
                          <NavIcon
                            size="1.1rem"
                            stroke={1.5}
                          />
                        </ActionIcon>
                        <span className="mt-1 text-center text-[10px] font-semibold tracking-tight text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]">
                          {navItem.label}
                        </span>
                      </Link>
                    : <>
                        <ActionIcon
                          component="div"
                          variant="subtle"
                          size="md"
                          color="gray"
                          className="text-[#3d4350] group-hover/navitem:bg-[var(--mantine-color-default-hover)] group-hover/navitem:text-[var(--mantine-color-gray-7)] dark:text-[var(--mantine-color-gray-4)] dark:group-hover/navitem:text-[var(--mantine-color-gray-4)]"
                        >
                          <NavIcon
                            size="1.1rem"
                            stroke={1.5}
                          />
                        </ActionIcon>
                        <span className="mt-1 text-center text-[10px] font-semibold tracking-tight text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]">
                          {navItem.label}
                        </span>
                      </>
                    }
                  </div>
                );
              }

              if (hasMenu) {
                return (
                  <Menu
                    key={navItem.label}
                    position="right-end"
                    withArrow
                  >
                    <Menu.Target>
                      <ActionIcon
                        variant="subtle"
                        size="md"
                        className="mb-4 text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]"
                        color="gray"
                      >
                        <NavIcon
                          size="1.1rem"
                          stroke={1.5}
                        />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {navItem.menuItems.map((item) => (
                        <Menu.Item
                          key={item.label}
                          leftSection={<item.icon size={14} />}
                          rightSection={item.rightSection}
                          onClick={item.onClick}
                        >
                          {item.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                );
              }

              return (
                <Tooltip
                  key={navItem.label}
                  label={navItem.label}
                  position="right"
                  withArrow
                >
                  {hasHref ?
                    <ActionIcon
                      component={Link}
                      href={readyToNav ? navItem.href(projectId) : '#'}
                      variant="subtle"
                      size="md"
                      className="mb-4 text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]"
                      color="gray"
                    >
                      <NavIcon
                        size="1.1rem"
                        stroke={1.5}
                      />
                    </ActionIcon>
                  : <ActionIcon
                      onClick={'onClick' in navItem ? navItem.onClick : undefined}
                      variant="subtle"
                      size="md"
                      className="mb-4 text-[#3d4350] dark:text-[var(--mantine-color-gray-4)]"
                      color="gray"
                    >
                      <NavIcon
                        size="1.1rem"
                        stroke={1.5}
                      />
                    </ActionIcon>
                  }
                </Tooltip>
              );
            })}
          </div>
        </AppShell.Section>
      </>
    );
  }

  return (
    <>
      <AppShell.Section
        grow
        component={ScrollArea}
        className={clsx(
          'group relative mb-4',
          viewMode === 'agent' &&
            '[&_.mantine-ScrollArea-scrollbar]:rounded-full [&_.mantine-ScrollArea-scrollbar]:bg-transparent [&_.mantine-ScrollArea-scrollbar]:opacity-0 [&_.mantine-ScrollArea-scrollbar]:transition-opacity [&_.mantine-ScrollArea-scrollbar]:duration-200 [&_.mantine-ScrollArea-scrollbar]:hover:bg-gray-100 [&_.mantine-ScrollArea-scrollbar]:dark:hover:bg-gray-800 [&_.mantine-ScrollArea-thumb]:rounded-full [&_.mantine-ScrollArea-thumb]:bg-gray-300 [&_.mantine-ScrollArea-thumb]:hover:bg-gray-400 [&_.mantine-ScrollArea-thumb]:dark:bg-gray-600 [&_.mantine-ScrollArea-thumb]:dark:hover:bg-gray-500 [&:hover_.mantine-ScrollArea-scrollbar]:opacity-100',
        )}
        scrollbarSize={viewMode === 'agent' ? 6 : 0}
        type={viewMode === 'agent' ? 'auto' : 'never'}
      >
        <div className="sticky top-0 z-10 bg-[var(--mantine-color-body)]">
          <div className="flex w-full items-center justify-between px-6 pt-5 pb-4">
            <FullLogo
              className="h-[28px]"
              color={colorScheme === 'dark' ? '#FCF5E5' : 'black'}
            />
          </div>
          {shouldShowProjectSwitcher && (
            <div className="px-3 pb-2">
              <Tooltip
                label="Jump to another project"
                withArrow
                position="right"
              >
                <div>
                  <SidebarProjectSwitcher
                    projectId={projectId}
                    projects={projectSwitcherOptions}
                    onProjectSwitch={(nextProjectId) => handleProjectSwitch(nextProjectId)}
                    onManageProject={() => void router.push(`/project/${projectId}/settings?tab=preferences`)}
                  />
                </div>
              </Tooltip>
            </div>
          )}
          {viewMode === 'agent' && (
            <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-4 translate-y-full bg-gradient-to-b from-[var(--mantine-color-body)] to-transparent" />
          )}
        </div>
        {/* <div className="mb-4 flex w-full justify-start gap-1">
          <div
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 px-2 py-[4px]"
            onClick={createTestHandler}
          >
            <IconPlus size={14} />
            <div className="text-sm">New Test</div>
          </div>
          <SidebarSearchButton />
        </div> */}
        <div className="pt-2" />
        {viewMode === 'agent' ?
          <>
            {getNavListItem(AGENT_NAV_ITEMS_TOP)}
            {getNavListItem([AGENT_NAV_ITEMS_RUNS])}
            {isAdmin && (
              <>
                <div className="flex items-center gap-2 pt-6 pb-1 pl-3 text-xs font-medium tracking-wide text-[var(--mantine-color-gray-6)] dark:text-[var(--mantine-color-gray-5)]">
                  Test Plan
                  <div className="pointer-events-none rounded-md border border-blue-300 bg-blue-100 px-[6px] py-[2px] text-[10px] font-semibold text-blue-900 dark:border-blue-500/50 dark:bg-blue-500/20 dark:text-blue-100">
                    BETA
                  </div>
                </div>
                {getNavListItem(AGENT_NAV_ITEMS_TCM_CHILDREN)}
              </>
            )}
            <div className="pt-6 pb-1 pl-3 text-xs font-medium tracking-wide text-[var(--mantine-color-gray-6)] dark:text-[var(--mantine-color-gray-5)]">
              Test Management
            </div>
            {getNavListItem(AGENT_NAV_ITEMS_PLAYWRIGHT)}
            <div className="pt-6 pb-1 pl-3 text-xs font-medium tracking-wide text-[var(--mantine-color-gray-6)] dark:text-[var(--mantine-color-gray-5)]">
              Configuration
            </div>
            {getNavListItem(AGENT_NAV_ITEMS_CONFIG)}
          </>
        : <>
            {getNavListItem(defaultNavItems)}
            <div className="pt-6 pb-1 pl-3 text-xs font-medium tracking-wide text-[var(--mantine-color-gray-6)] dark:text-[var(--mantine-color-gray-5)]">
              Test Creation
            </div>
            {getNavListItem(creationNavItems)}
            <div className="pt-6 pb-1 pl-3 text-xs font-medium tracking-wide text-[var(--mantine-color-gray-6)] dark:text-[var(--mantine-color-gray-5)]">
              Execution
            </div>
            {getNavListItem(executionNavItems)}
          </>
        }
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-4 bg-gradient-to-t from-[var(--mantine-color-body)] to-transparent" />
      </AppShell.Section>
      <AppShell.Section>
        <UsageLimitIndicator />
        {footerNavItems.map((navItem) => {
          const NavIcon = navItem.icon;
          const hasHref = 'href' in navItem && navItem.href;
          const hasMenu = 'menuItems' in navItem && navItem.menuItems;

          if (hasMenu) {
            return (
              <Menu
                key={navItem.label}
                position="right-end"
                withArrow
              >
                <Menu.Target>
                  <NavLink
                    component="button"
                    active={false}
                    label={navItem.label}
                    leftSection={
                      <NavIcon
                        size="1rem"
                        stroke={1.5}
                      />
                    }
                    classNames={{
                      label: `${viewMode === 'agent' ? 'text-[13px]' : 'text-[12px]'} font-normal text-gray-800 dark:text-gray-100`,
                      section: 'text-gray-700 dark:text-gray-200',
                      root: 'rounded-[0.2rem] px-3 py-2 my-[0.3rem] [&:hover_.mantine-NavLink-label]:text-[var(--mantine-color-text)] [&:hover_.mantine-NavLink-section]:text-[var(--mantine-color-text)]',
                    }}
                  />
                </Menu.Target>
                <Menu.Dropdown>
                  {navItem.menuItems.map((item) => (
                    <Menu.Item
                      key={item.label}
                      leftSection={<item.icon size={14} />}
                      rightSection={item.rightSection}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            );
          }

          return (
            <NavLink
              key={navItem.label}
              active={false}
              label={navItem.label}
              component={Link}
              href={
                hasHref ?
                  readyToNav ?
                    navItem.href(projectId)
                  : '#'
                : '#'
              }
              onClick={hasHref || !('onClick' in navItem) ? undefined : navItem.onClick}
              leftSection={
                <NavIcon
                  size="1rem"
                  stroke={1.5}
                />
              }
              classNames={{
                label: `${viewMode === 'agent' ? 'text-[13px]' : 'text-[12px]'} font-normal text-gray-800 dark:text-gray-100`,
                section: 'text-gray-700 dark:text-gray-200',
                root: 'rounded-[0.2rem] px-3 py-2 my-[0.3rem] [&:hover_.mantine-NavLink-label]:text-[var(--mantine-color-text)] [&:hover_.mantine-NavLink-section]:text-[var(--mantine-color-text)]',
              }}
            />
          );
        })}
        <div className="px-1 pt-3 pb-2">
          <ColorSchemeToggle />
        </div>
        {viewMode === 'agent' && orgMetadata?.defaultViewMode !== 'agent' && (
          <div className="w-full px-3 pt-1 pb-3">
            <ViewModeSwitcher fullWidth />
          </div>
        )}
      </AppShell.Section>
    </>
  );
}
